import { Children, isValidElement, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search } from "lucide-react";

// Lista desplegable de la app en lugar del <select> del navegador: mismo uso (value,
// onChange y <option>/<optgroup> como hijos), con buscador cuando hay muchas opciones,
// teclado y, en el celular, una hoja que sube desde abajo. Debajo queda un <select>
// oculto sincronizado para los formularios y las pruebas.

const CON_BUSCADOR = 7;
const ES_CELULAR = "(max-width: 639px)";

const textoDe = (hijos) =>
  Children.toArray(hijos).map((h) => (typeof h === "string" || typeof h === "number" ? String(h) : textoDe(h?.props?.children))).join("");

function leerOpciones(hijos, grupo = null, lista = []) {
  Children.toArray(hijos).forEach((hijo) => {
    if (!isValidElement(hijo)) return;
    if (hijo.type === "optgroup") {
      leerOpciones(hijo.props.children, hijo.props.label, lista);
    } else if (hijo.type === "option") {
      const etiqueta = textoDe(hijo.props.children);
      lista.push({
        valor: String(hijo.props.value ?? etiqueta),
        etiqueta,
        deshabilitada: Boolean(hijo.props.disabled),
        grupo,
      });
    } else if (hijo.props?.children) {
      leerOpciones(hijo.props.children, grupo, lista); // Fragmentos.
    }
  });
  return lista;
}

const sinTildes = (texto) => texto.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

function Selector({ value, onChange, children, className = "", disabled, name, id, "aria-label": ariaLabel, ...resto }) {
  const opciones = useMemo(() => leerOpciones(children), [children]);
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [activa, setActiva] = useState(0);
  const [posicion, setPosicion] = useState(null);
  // Nombre del botón para lectores de pantalla: el aria-label o el texto de su <label for>.
  const [nombreAccesible, setNombreAccesible] = useState(ariaLabel);
  const boton = useRef(null);
  const lista = useRef(null);
  const buscador = useRef(null);
  const idLista = useId();

  const valorActual = value === undefined || value === null ? "" : String(value);
  const elegida = opciones.find((o) => o.valor === valorActual);
  const texto = sinTildes(busqueda.trim());
  const visibles = texto ? opciones.filter((o) => sinTildes(o.etiqueta).includes(texto)) : opciones;
  const conBuscador = opciones.length >= CON_BUSCADOR;

  const emitir = (valor) => {
    const objetivo = { value: valor, name, id };
    onChange?.({ target: objetivo, currentTarget: objetivo, preventDefault() {}, stopPropagation() {} });
  };

  const cerrar = useCallback((devolverFoco = true) => {
    setAbierto(false);
    setPosicion(null);
    setBusqueda("");
    if (devolverFoco) boton.current?.focus();
  }, []);

  const elegir = (opcion) => {
    if (!opcion || opcion.deshabilitada) return;
    if (opcion.valor !== valorActual) emitir(opcion.valor);
    cerrar();
  };

  // Ubica la lista debajo (o encima, si no cabe) del botón. Va en un portal para que
  // ninguna ventana o panel con desplazamiento la recorte.
  const ubicar = useCallback(() => {
    if (!boton.current) return;
    if (window.matchMedia?.(ES_CELULAR).matches) return setPosicion({ celular: true });
    const caja = boton.current.getBoundingClientRect();
    const alto = Math.min(320, window.innerHeight * 0.5);
    const arriba = caja.bottom + alto + 8 > window.innerHeight && caja.top > alto;
    setPosicion({
      left: caja.left,
      width: Math.max(caja.width, 220),
      top: arriba ? undefined : caja.bottom + 6,
      bottom: arriba ? window.innerHeight - caja.top + 6 : undefined,
    });
  }, []);

  useLayoutEffect(() => {
    if (!abierto) return undefined;
    ubicar();
    window.addEventListener("resize", ubicar);
    window.addEventListener("scroll", ubicar, true);
    return () => {
      window.removeEventListener("resize", ubicar);
      window.removeEventListener("scroll", ubicar, true);
    };
  }, [abierto, ubicar]);

  // Cuando la lista ya está dibujada (tiene posición): foco al buscador y a la opción elegida.
  const dibujada = abierto && Boolean(posicion);
  useEffect(() => {
    if (!dibujada) return undefined;
    const indice = Math.max(0, visibles.findIndex((o) => o.valor === valorActual));
    setActiva(indice);
    (conBuscador ? buscador.current : lista.current)?.focus();
    const alPresionar = (e) => {
      if (boton.current?.contains(e.target) || lista.current?.parentElement?.contains(e.target)) return;
      cerrar(false);
    };
    document.addEventListener("mousedown", alPresionar);
    return () => document.removeEventListener("mousedown", alPresionar);
    // Solo al abrir: la opción activa inicial es la elegida.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dibujada]);

  useEffect(() => {
    lista.current?.querySelector(`[data-indice="${activa}"]`)?.scrollIntoView?.({ block: "nearest" });
  }, [activa]);

  useEffect(() => {
    if (ariaLabel || !id) return setNombreAccesible(ariaLabel);
    setNombreAccesible(document.querySelector(`label[for="${window.CSS?.escape ? CSS.escape(id) : id}"]`)?.textContent?.trim() || undefined);
  }, [ariaLabel, id]);

  const teclado = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiva((i) => Math.min(visibles.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiva((i) => Math.max(0, i - 1)); }
    else if (e.key === "Home") { e.preventDefault(); setActiva(0); }
    else if (e.key === "End") { e.preventDefault(); setActiva(visibles.length - 1); }
    else if (e.key === "Enter") { e.preventDefault(); elegir(visibles[activa]); }
    else if (e.key === "Escape" || e.key === "Tab") { e.preventDefault(); cerrar(); }
  };

  const alTeclearEnBoton = (e) => {
    resto.onKeyDown?.(e);
    if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
      e.preventDefault();
      if (!disabled) setAbierto(true);
    }
  };

  const listado = abierto && posicion && createPortal(
    <div className={posicion.celular ? "fixed inset-0 z-[90] flex items-end bg-black/60 animate-aparecer" : ""}>
      <div
        className={posicion.celular
          ? "ventana-panel w-full rounded-t-3xl border-t border-white/10 bg-zinc-950 p-3 pb-6"
          : "fixed z-[90] animate-escala rounded-2xl border border-white/15 bg-zinc-900/95 p-1.5 shadow-2xl shadow-black/60 backdrop-blur-md"}
        style={posicion.celular ? undefined : { left: posicion.left, width: posicion.width, top: posicion.top, bottom: posicion.bottom }}
      >
        {posicion.celular && <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/20" aria-hidden="true" />}
        {conBuscador && (
          <div className="relative mb-1.5">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" aria-hidden="true" />
            <input
              ref={buscador}
              value={busqueda}
              onChange={(e) => { setBusqueda(e.target.value); setActiva(0); }}
              onKeyDown={teclado}
              placeholder="Buscar…"
              aria-label="Buscar opción"
              aria-controls={idLista}
              aria-activedescendant={visibles[activa] ? `${idLista}-${activa}` : undefined}
              className="w-full rounded-xl border border-white/10 bg-black/40 py-2 pl-9 pr-3 text-sm text-white placeholder-gray-500 focus:border-white/40 focus:outline-none"
            />
          </div>
        )}
        <ul
          ref={lista}
          id={idLista}
          role="listbox"
          tabIndex={-1}
          onKeyDown={teclado}
          aria-label={ariaLabel}
          className={`overflow-y-auto overscroll-contain focus:outline-none ${posicion.celular ? "max-h-[55vh]" : "max-h-72"}`}
        >
          {visibles.map((opcion, i) => {
            const nuevoGrupo = opcion.grupo && opcion.grupo !== visibles[i - 1]?.grupo;
            const seleccionada = opcion.valor === valorActual;
            return (
              <li key={`${opcion.grupo}-${opcion.valor}-${i}`} role="presentation">
                {nuevoGrupo && (
                  <p className="px-3 pb-1 pt-2.5 text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">{opcion.grupo}</p>
                )}
                <div
                  id={`${idLista}-${i}`}
                  data-indice={i}
                  role="option"
                  aria-selected={seleccionada}
                  aria-disabled={opcion.deshabilitada || undefined}
                  onMouseEnter={() => setActiva(i)}
                  onClick={() => elegir(opcion)}
                  className={`flex cursor-pointer items-center justify-between gap-3 rounded-xl px-3 text-sm transition-colors ${posicion.celular ? "py-3" : "py-2"} ${
                    opcion.deshabilitada ? "cursor-not-allowed text-gray-600" : i === activa ? "bg-white/10 text-white" : "text-gray-300"
                  } ${seleccionada ? "font-semibold text-white" : ""}`}
                >
                  <span className="truncate">{opcion.etiqueta || "—"}</span>
                  {seleccionada && <Check size={15} className="shrink-0" aria-hidden="true" />}
                </div>
              </li>
            );
          })}
          {!visibles.length && <li className="px-3 py-3 text-sm text-gray-500">Nada coincide con «{busqueda}».</li>}
        </ul>
      </div>
    </div>,
    document.body,
  );

  return (
    <>
      <button
        {...resto}
        ref={boton}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={abierto}
        aria-controls={abierto ? idLista : undefined}
        aria-label={nombreAccesible ? `${nombreAccesible}: ${elegida?.etiqueta || "sin elegir"}` : elegida?.etiqueta}
        disabled={disabled}
        onClick={(e) => { resto.onClick?.(e); if (!disabled) setAbierto((a) => !a); }}
        onKeyDown={alTeclearEnBoton}
        className={`${className} inline-flex items-center justify-between gap-2 text-left disabled:cursor-not-allowed`}
      >
        <span className={`truncate ${elegida ? "" : "text-gray-500"}`}>{elegida?.etiqueta || opciones[0]?.etiqueta || "Seleccionar"}</span>
        <ChevronDown size={16} aria-hidden="true" className={`shrink-0 text-gray-500 transition-transform duration-200 ${abierto ? "rotate-180" : ""}`} />
      </button>
      {/* Respaldo invisible: lleva el id y el aria-label (su <label for> lo encuentra), sirve a
          los formularios y a las pruebas, y si recibe el foco lo pasa al botón visible. */}
      <select
        id={id}
        name={name}
        value={valorActual}
        onChange={(e) => onChange?.(e)}
        onFocus={() => boton.current?.focus()}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-hidden="true"
        tabIndex={-1}
        className="sr-only"
      >
        {children}
      </select>
      {listado}
    </>
  );
}

export default Selector;
